/**
 * Draw.io XML parser → IRGraph.
 * Handles both raw mxGraphModel XML and compressed mxfile format.
 */

import type { IRGraph, IRNode, IREdge, IRLane } from '../types';
import { NodeShape, createEmptyGraph } from '../types';
import { shapeFromStyle, SHAPE_REGISTRY } from '../registry';

/**
 * Parse a draw.io XML string into an IRGraph.
 * Supports both compressed (mxfile with deflated diagram) and uncompressed XML.
 */
export function parseDrawioXml(xml: string): IRGraph {
  const graph = createEmptyGraph('TD', 'flowchart');
  const parser = new DOMParser();

  let graphModel: Element | null = null;

  const doc = parser.parseFromString(xml, 'text/xml');
  const mxfile = doc.querySelector('mxfile');

  if (mxfile) {
    const diagram = mxfile.querySelector('diagram');
    if (diagram) {
      const innerModel = diagram.querySelector('mxGraphModel');
      if (innerModel) {
        graphModel = innerModel;
      } else {
        // Compressed content — try to decompress
        const compressed = diagram.textContent?.trim();
        if (compressed) {
          const decompressed = decompressMxfile(compressed);
          const innerDoc = parser.parseFromString(decompressed, 'text/xml');
          graphModel = innerDoc.querySelector('mxGraphModel');
        }
      }
    }
  } else {
    graphModel = doc.querySelector('mxGraphModel');
  }

  if (!graphModel) return graph;

  const root = graphModel.querySelector('root');
  if (!root) return graph;

  const cells = root.querySelectorAll('mxCell');

  // First pass: identify containers (swimlanes) and nodes
  const cellMap = new Map<string, Element>();
  const containerIds = new Set<string>();

  for (const cell of cells) {
    const id = cell.getAttribute('id');
    if (!id) continue;
    cellMap.set(id, cell);

    const style = cell.getAttribute('style') ?? '';
    if (style.includes('swimlane') && cell.getAttribute('vertex') === '1') {
      containerIds.add(id);
    }
  }

  // Determine lane hierarchy:
  // A swimlane whose parent is also a swimlane = lane
  // A swimlane whose parent is "1" or root = container
  const laneMap = new Map<string, IRLane>();

  for (const cell of cells) {
    const id = cell.getAttribute('id');
    const style = cell.getAttribute('style') ?? '';
    const parent = cell.getAttribute('parent') ?? '';
    if (!id) continue;

    const isVertex = cell.getAttribute('vertex') === '1';
    const isEdge = cell.getAttribute('edge') === '1';

    if (isVertex && style.includes('swimlane')) {
      // Check if parent is also a swimlane container
      if (containerIds.has(parent)) {
        const geo = cell.querySelector('mxGeometry');
        const lane: IRLane = {
          id,
          title: cell.getAttribute('value') ?? id,
          nodeIds: [],
          x: parseFloat(geo?.getAttribute('x') ?? '0'),
          y: parseFloat(geo?.getAttribute('y') ?? '0'),
          width: parseFloat(geo?.getAttribute('width') ?? '0'),
          height: parseFloat(geo?.getAttribute('height') ?? '0'),
        };
        laneMap.set(id, lane);
        graph.lanes.push(lane);
      }
    }
  }

  // Second pass: extract nodes (vertices that aren't containers/lanes)
  for (const cell of cells) {
    const id = cell.getAttribute('id');
    if (!id || id === '0' || id === '1') continue;

    const style = cell.getAttribute('style') ?? '';
    const parent = cell.getAttribute('parent') ?? '';
    const isVertex = cell.getAttribute('vertex') === '1';

    if (isVertex && !containerIds.has(id) && !laneMap.has(id)) {
      const shape = shapeFromStyle(style);
      const entry = SHAPE_REGISTRY[shape];
      const geo = cell.querySelector('mxGeometry');

      const node: IRNode = {
        id,
        label: stripHtml(cell.getAttribute('value') ?? ''),
        shape,
        width: parseFloat(geo?.getAttribute('width') ?? String(entry.defaultWidth)),
        height: parseFloat(geo?.getAttribute('height') ?? String(entry.defaultHeight)),
        x: parseFloat(geo?.getAttribute('x') ?? '0'),
        y: parseFloat(geo?.getAttribute('y') ?? '0'),
      };

      // Assign to lane if parent is a lane
      if (laneMap.has(parent)) {
        node.laneId = parent;
        laneMap.get(parent)!.nodeIds.push(id);
      }

      graph.nodes.set(id, node);
    }
  }

  // Third pass: extract edges
  let edgeCounter = 0;
  for (const cell of cells) {
    const id = cell.getAttribute('id');
    if (!id) continue;

    if (cell.getAttribute('edge') === '1') {
      const source = cell.getAttribute('source') ?? '';
      const target = cell.getAttribute('target') ?? '';
      const style = cell.getAttribute('style') ?? '';
      const value = cell.getAttribute('value') ?? '';

      if (source && target) {
        edgeCounter++;
        const edge: IREdge = {
          id: `e${edgeCounter}`,
          source,
          target,
          label: value || undefined,
          dashed: style.includes('dashed=1'),
        };

        // Determine cross-lane
        const srcNode = graph.nodes.get(source);
        const tgtNode = graph.nodes.get(target);
        if (srcNode && tgtNode) {
          edge.crossLane = srcNode.laneId !== tgtNode.laneId;
        }

        graph.edges.push(edge);
      }
    }
  }

  return graph;
}

/** Decompress a base64 + deflated mxfile diagram content */
function decompressMxfile(compressed: string): string {
  try {
    // Dynamic import would be better but we need sync here
    // Decode base64
    const binary = atob(compressed);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    // Use pako to inflate
    const pako = (globalThis as any).__pako;
    if (pako) {
      const inflated = pako.inflateRaw(bytes, { to: 'string' });
      return decodeURIComponent(inflated);
    }

    // Fallback: try DecompressionStream API
    return compressed;
  } catch {
    return compressed;
  }
}

function stripHtml(s: string): string {
  return s.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '').trim();
}
